// DocumentsHomePage — layout shell for `/documents/*`. Owns the
// Documents/Templates tab toggle and renders the matched child via
// `<Outlet />`. Selection lives on path segments:
//
//   /documents/<path>
//   /documents/f/<folderPath>
//   /documents/templates/<path>
//   /documents/templates/f/<folderPath>
//
// The tab strip is forwarded into each sub-page as a `topSlot` so
// it ends up rendered ABOVE the sidebar header (rather than across
// the whole page width).

import { useEffect, useMemo, type ReactNode } from 'react'
import { FileText, LayoutTemplate } from 'lucide-react'
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { ExplorerTabs, type ExplorerTab } from '../../components/explorer'
import { DOC_TEMPLATES_HOME, DOCS_HOME } from './explorer-paths'

export type DocumentsView = 'documents' | 'templates'

export interface DocumentsOutletContext {
  tabsSlot: ReactNode
}

const DOCUMENT_TABS: ReadonlyArray<ExplorerTab<DocumentsView>> = [
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'templates', label: 'Templates', icon: LayoutTemplate }
]

function readView(pathname: string): DocumentsView {
  return pathname === DOC_TEMPLATES_HOME || pathname.startsWith(`${DOC_TEMPLATES_HOME}/`)
    ? 'templates'
    : 'documents'
}

const DocumentsHomePage = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [searchParams] = useSearchParams()
  const view = readView(pathname)

  // Legacy `?view=templates` → `/documents/templates` (keep other params
  // so `?file=` / `?folder=` can still be rewritten by the templates explorer).
  useEffect(() => {
    if (searchParams.get('view') !== 'templates') return
    if (pathname === DOC_TEMPLATES_HOME || pathname.startsWith(`${DOC_TEMPLATES_HOME}/`)) {
      return
    }
    const next = new URLSearchParams(searchParams)
    next.delete('view')
    const search = next.toString()
    navigate(
      { pathname: DOC_TEMPLATES_HOME, search: search === '' ? '' : `?${search}` },
      { replace: true }
    )
  }, [searchParams, pathname, navigate])

  const tabsSlot = useMemo(
    () => (
      <ExplorerTabs
        tabs={DOCUMENT_TABS}
        active={view}
        onChange={(next) => {
          navigate(next === 'templates' ? DOC_TEMPLATES_HOME : DOCS_HOME)
        }}
        testIdPrefix="documents-tab"
      />
    ),
    [view, navigate]
  )

  return <Outlet context={{ tabsSlot } satisfies DocumentsOutletContext} />
}

export default DocumentsHomePage
